import { ElementType } from '../../utils/enums.ts';
import Autocomplete, { IAutocompleteOption } from '../common/autocomplete.tsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import {
  GET_FILTERED_QUIZZES,
  GET_FILTERED_TESTS,
} from '../../apollo/answers-view.ts';
import { Sort } from '../../apollo/__generated__/graphql.ts';
import { getAutocompleteKey } from '../../utils/common-utils.ts';

type IProps = {
  type: ElementType;
  onChange: (element?: IAutocompleteOption) => void;
  onChangeType: (type: ElementType) => void;
  courseId?: number;
};

export const ElementSelect = ({
  type,
  onChange,
  onChangeType,
  courseId,
}: IProps) => {
  const [quizName, setQuizName] = useState<string>();
  const [testName, setTestName] = useState<string>();
  const [element, setElement] = useState<IAutocompleteOption>();
  const { data: quizzesData, loading: loadingQuizzes } = useQuery(
    GET_FILTERED_QUIZZES,
    {
    variables: {
      paging: { limit: 50 },
      filter: {
        name: quizName,
        ...(courseId && { courseId }),
      },
      sort: { name: Sort.Asc },
    },
    },
  );
  const { data: testsData, loading: loadingTests } = useQuery(
    GET_FILTERED_TESTS,
    {
    variables: {
      paging: { limit: 50 },
      filter: {
        name: testName,
        ...(courseId && { courseId }),
      },
      sort: { name: Sort.Asc },
    },
    },
  );

  const quizzesOptions = useMemo(() => {
    return quizzesData?.quiz?.getQuizzes?.results.map((quiz) => ({
      title: quiz.name,
      key: quiz.id.toString(),
    }));
  }, [quizzesData?.quiz?.getQuizzes?.results]);

  const testsOptions = useMemo(() => {
    return testsData?.test?.getTests?.results.map((test) => ({
      title: test.name,
      key: test.id.toString(),
    }));
  }, [testsData?.test?.getTests?.results]);

  const onChangeValue = useCallback(
    (value?: string) => {
      switch (type) {
        case ElementType.QUIZ:
          setQuizName(value);
          break;
        case ElementType.TEST:
          setTestName(value);
          break;
      }
    },
    [type],
  );

  const setValue = useCallback(
    (value: IAutocompleteOption | IAutocompleteOption[]) => {
      if (Array.isArray(value)) return;
      if (getAutocompleteKey(value) === getAutocompleteKey(element)) return;
      onChange(value);
      setElement(value);
    },
    [element, onChange],
  );

  const onClear = useCallback(() => {
    setElement(undefined);
    setQuizName(undefined);
    setTestName(undefined);
    onChange(undefined);
  }, [onChange]);

  useEffect(() => {
    setElement(undefined);
    setQuizName(undefined);
    setTestName(undefined);
  }, [courseId, type]);

  useEffect(() => {
    if (element || quizName || testName) {
      return;
    }

    if (loadingQuizzes || loadingTests) {
      return;
    }

    const hasQuizzes = !!quizzesOptions?.length;
    const hasTests = !!testsOptions?.length;

    if (type === ElementType.QUIZ && !hasQuizzes && hasTests) {
      onChangeType(ElementType.TEST);
    }

    if (type === ElementType.TEST && !hasTests && hasQuizzes) {
      onChangeType(ElementType.QUIZ);
    }
  }, [
    element,
    loadingQuizzes,
    loadingTests,
    onChangeType,
    quizName,
    quizzesOptions?.length,
    testName,
    testsOptions?.length,
    type,
  ]);

  return (
    <div className="relative h-10 w-full">
      <Autocomplete
        value={element}
        className={
          'h-10 w-full [&>div]:border [&>div]:border-[#dfe1e5] [&>div]:shadow-sm [&>div>input]:pr-16 [&>div>input]:placeholder:text-gray [&>div>input]:placeholder:opacity-60'
        }
        placeholder={
          type === ElementType.TEST ? 'Select test...' : 'Select quiz...'
        }
        leadingIcon={<MagnifyingGlassIcon className="h-4 w-4 text-gray" />}
        options={type === ElementType.TEST ? testsOptions : quizzesOptions}
        setValue={setValue}
        onChangeValue={onChangeValue}
      />
      {!!element && (
        <button
          aria-label={`Clear ${type === ElementType.TEST ? 'test' : 'quiz'}`}
          className="absolute right-8 top-1/2 z-[1] flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-gray transition hover:bg-light-gray hover:text-black"
          onClick={onClear}
          type="button"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
